import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Table } from 'reactstrap';

import apiOffers from 'api/offers';
import IProductInstallationCategory from 'api/interfaces/products/IProductInstallationCategory';
import formatGroupedNumber from 'services/numbers/formatGroupedNumber';
import { useCurrency } from 'services/offers/currency';
import {
  OTHER_EXPENSE_CATEGORY,
  getChoiceList,
  useCategoryChoices,
} from 'services/offers/offerChoices';
import translations from 'services/translations/translations';

import { OTHER_EXPENSE } from '../../../../services/installationTypes';
import { getMarginClass } from '../getMarginRatioClassName';
import IInstallation from '../../IInstallation';
import ISubproduct from '../../ISubproduct';
import getSupplierSummary from './getSupplierSummary';

/**
 * An internal help table under the reference prices: the selling price, the expense and
 * the margin of the calculation rows summed by supplier. Not printed.
 */
const SupplierSummary = ({
  installations,
  offerProductId,
  quantity,
  subproducts,
}: {
  installations: IInstallation[];
  offerProductId?: number;
  quantity: number;
  subproducts: ISubproduct[];
}): JSX.Element => {
  const { t } = useTranslation([
    translations.offers,
    translations.common,
    translations.products,
  ]);
  const currency = useCurrency();
  const text = (key: string) =>
    t(`${translations.calculationWindow}:supplierSummary:${key}`);

  const installationCategories: IProductInstallationCategory[] = useSelector(
    (state: any) => state.products.installationCategories
  );

  const [department, setDepartment] = useState<null | string>(null);
  const [suppliers, setSuppliers] = useState<Map<number, string>>(new Map());

  // Fetched again only when the products of the rows change
  const productIds = useMemo(
    () =>
      Array.from(
        new Set(
          subproducts
            // The products of the sub rows too: a material priced from them is shared
            // by their suppliers
            .flatMap((subproduct) => [
              subproduct.product,
              ...subproduct.details.map((detail) => detail.product),
            ])
            .map((product) => Number(product?.value))
            .filter((id) => id > 0)
        )
      ).sort((a, b) => a - b),
    [subproducts]
  );
  const productIdsKey = productIds.join(',');

  useEffect(() => {
    let cancelled = false;
    apiOffers.products
      .suppliers(offerProductId, productIds)
      .then((result) => {
        if (cancelled) {
          return;
        }
        setDepartment(result.department);
        setSuppliers(
          new Map(result.suppliers.map((row) => [row.productId, row.name]))
        );
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerProductId, productIdsKey]);

  // The name of the category of an other expense as in the choice lists of the admins
  const categoryChoices = useCategoryChoices();
  const otherExpenseCategory = (installation: IInstallation): null | string =>
    !installation.category
      ? null
      : getChoiceList(
          categoryChoices.choices,
          OTHER_EXPENSE_CATEGORY,
          installation.category,
          categoryChoices.language
        ).find((choice) => choice.value === installation.category)?.label ??
        t(
          `${translations.products}:installationCategories:${installation.category}`,
          { defaultValue: installation.category }
        );

  const rows = getSupplierSummary({
    installations,
    isOtherExpense: (installation) =>
      (installationCategories ?? []).some(
        (category) =>
          category.type === OTHER_EXPENSE &&
          category.category === installation.category
      ),
    otherExpenseCategory,
    ownWork: department ?? text('Own work'),
    quantity,
    subproducts,
    supplierOfProduct: (productId) => suppliers.get(productId) ?? null,
  });

  return (
    <div className="supplier-summary">
      <p className="version-details-title">{text('Title')}</p>
      <p className="reference-info">{text('Info')}</p>
      {rows.length === 0 ? (
        <p className="reference-info">{text('No rows')}</p>
      ) : (
        <Table size="sm" className="reference-table">
          <thead>
            <tr>
              <th>{text('Supplier')}</th>
              <th className="number">{text('Selling price')}</th>
              <th className="number">{text('Expense')}</th>
              <th className="number">{text('Margin')}</th>
              <th className="number">{text('Margin %')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={`${row.otherExpense ? 'other-expense:' : ''}${
                  row.supplier ?? ''
                }`}
              >
                <td>
                  {row.supplier === null
                    ? text('Unknown')
                    : `${row.supplier}${
                        row.otherExpense ? ` (${text('Other expense')})` : ''
                      }`}
                </td>
                <td className="number key-figure">
                  {formatGroupedNumber(row.sellingPrice)} {currency}
                </td>
                <td className="number">
                  {formatGroupedNumber(row.expense)} {currency}
                </td>
                <td className="number">
                  {formatGroupedNumber(row.contributionMargin)} {currency}
                </td>
                <td
                  className={`number key-figure ${
                    row.sellingPrice === 0
                      ? ''
                      : getMarginClass(row.contributionMarginRatio)
                  }`}
                >
                  {formatGroupedNumber(row.contributionMarginRatio)} %
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
};

export default SupplierSummary;
